
import { useEffect, useState, useRef, useCallback } from 'react';
import { Poll } from '@/types/poll';

interface UseInfiniteScrollProps {
  items: Poll[];
  itemsPerPage?: number;
}

export const useInfinitePolls = ({
  items,
  itemsPerPage = 10
}: UseInfiniteScrollProps) => {
  const [displayedItems, setDisplayedItems] = useState<Poll[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  
  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  // Reset when items change
  useEffect(() => {
    setDisplayedItems(items.slice(0, itemsPerPage));
    setCurrentPage(1);
    setHasMore(items.length > itemsPerPage);
  }, [items, itemsPerPage]);

  // Load more function
  const loadMore = useCallback(() => {
    if (currentPage >= totalPages) {
      setHasMore(false);
      return;
    }

    setIsLoading(true);
    
    // Simulate network delay for smoother UX
    setTimeout(() => {
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, items.length);
      
      setDisplayedItems(prevItems => [
        ...prevItems,
        ...items.slice(startIndex, endIndex)
      ]);
      
      setCurrentPage(nextPage);
      setHasMore(nextPage < totalPages);
      setIsLoading(false);
    }, 300);
  }, [currentPage, totalPages, items, itemsPerPage]);

  // Last element ref callback
  const lastItemRef = useCallback((node: HTMLElement | null) => {
    if (isLoading) return;
    
    if (observer.current) {
      observer.current.disconnect();
    }
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    }, { threshold: 0.5 });
    
    if (node) {
      observer.current.observe(node);
    }
  }, [isLoading, hasMore, loadMore]);

  return {
    displayedItems,
    hasMore,
    isLoading,
    loadMore,
    lastItemRef,
    currentPage,
    totalPages,
    totalItems: items.length
  };
};
